import { create } from "zustand";
import { DEFAULT_LANGUAGE, type Language } from "@/src/i18n/language";
import { t } from "@/src/i18n";
import { addPolygon, addRoom, subtractPolygon, subtractRoom } from "@/src/core/geometry/boolean";
import { getStampDefinition } from "@/src/core/engine/stampCatalog";
import { DEFAULT_WALL_TYPE, getWallObject, nextWallType } from "@/src/core/engine/wallObjects";
import {
  DEFAULT_FLOOR_TYPE,
  FLOOR_OBJECT_ASSET,
  FLOOR_ROTATION_STEP,
  getFloorObject,
} from "@/src/core/engine/floorObjects";
import {
  DEFAULT_MARKER_TYPE,
  MARKER_OBJECT_ASSET,
  getMarkerObject,
} from "@/src/core/engine/markerObjects";
import { DEFAULT_PATCH_TYPE, type PatchShape } from "@/src/core/engine/terrainPatches";
import {
  cloneRoomKeys,
  mergeRoomKey,
  roomKeysEqual,
  type RoomKeyUpdate,
} from "@/src/core/roomKeys";
import {
  assignMissingObjectModes,
  defaultToolForMode,
  gridSizeForMode,
  isToolAvailableInMode,
  objectBelongsToMode,
} from "@/src/core/mapMode";
import type {
  ActiveTool,
  AnnotationKind,
  HexCell,
  MapDocument,
  MapMeta,
  MapMode,
  MapObject,
  MultiPolygon,
  Point,
  Polygon,
  Rect,
  RoomKeyMap,
  StampAssetId,
  TerrainBrush,
  Viewport,
  WallObjectType,
  FloorObjectType,
  MarkerObjectType,
  TerrainPatch,
  TerrainPatchType,
} from "@/src/types/map";

const defaultMeta: MapMeta = {
  // Module-init default: t() cannot run here because the store it reads from is
  // still being created. bootstrapSession() localises this for a fresh visitor.
  title: "The Sunken Archive",
  gridSizeWorld: 50,
  dungeonGridSizeWorld: 50,
  hexGridSizeWorld: 50,
  printGridSizeMm: 25.4,
  gridType: "square",
  width: 3000,
  height: 2000,
  backgroundColor: "#e9dfc8",
  wallColor: "#211c17",
  floorColor: "#f8f2e4",
  hatchColor: "#4a4037",
  showGrid: true,
  showHatching: true,
  showHexCoordinates: false,
  wallWidth: 4,
};

const sampleGeometry: MultiPolygon = [
  [
    [
      [-200, -150], [150, -150], [150, -50], [350, -50], [350, 100],
      [150, 100], [150, 300], [-50, 300], [-50, 150], [-200, 150], [-200, -150],
    ],
  ],
];

interface MapSnapshot {
  geometry: MultiPolygon;
  hexCells: HexCell[];
  objects: MapObject[];
  terrainPatches: TerrainPatch[];
  roomKeys: RoomKeyMap;
}

interface MapState {
  /** UI language. App-scoped: it follows the user, not the map. */
  language: Language;
  /** Id of the map record this store is currently editing; null before hydration. */
  currentMapId: string | null;
  /** False until the persistence layer has loaded (or decided not to load) a map. */
  hydrated: boolean;
  tool: ActiveTool;
  meta: MapMeta;
  floorPolygons: MultiPolygon;
  hexCells: HexCell[];
  objects: MapObject[];
  /** Painted terrain regions, drawn above the floor and below objects. */
  terrainPatches: TerrainPatch[];
  /**
   * Adventure keys, keyed by the id of the room-number annotation each binds to.
   * Map data: it snapshots for undo and joins autosave's fingerprint.
   */
  roomKeys: RoomKeyMap;
  /**
   * Which room the key panel is editing, or null when it is closed. Ephemeral
   * editor state like `selectedObjectId`, so it is deliberately NOT part of the
   * autosave fingerprint — a panel being open is not a change to the map.
   */
  roomKeyPanelObjectId: string | null;
  viewport: Viewport;
  selectedStampAsset: StampAssetId;
  /** Which member of the door & passage family the stamp tool places. */
  selectedWallType: WallObjectType;
  /** Which member of the vertical-movement family the stamp tool places. */
  selectedFloorType: FloorObjectType;
  /** Which member of the marker & furniture family the stamp tool places. */
  selectedMarkerType: MarkerObjectType;
  /** Which terrain patch the patch tool paints, and in which shape. */
  selectedPatchType: TerrainPatchType;
  patchShape: PatchShape;
  patchEraseMode: boolean;
  /** Rotation applied to the next grid-snapped floor object, in degrees. */
  floorPlacementRotation: number;
  selectedTerrain: TerrainBrush;
  repeatPlacement: boolean;
  annotationKind: AnnotationKind;
  annotationText: string;
  annotationFontSize: number;
  annotationColor: string;
  nextRoomNumber: number;
  selectedObjectId: string | null;
  past: MapSnapshot[];
  future: MapSnapshot[];
  terrainStrokeSnapshot: MapSnapshot | null;
  setTool: (tool: ActiveTool) => void;
  setMapMode: (mode: MapMode) => void;
  setViewport: (viewport: Viewport) => void;
  setTitle: (title: string) => void;
  updateMeta: (updates: Partial<MapMeta>) => void;
  setPrintGridSizeMm: (size: number) => void;
  setSelectedStampAsset: (assetId: StampAssetId) => void;
  setSelectedWallType: (type: WallObjectType) => void;
  cycleWallType: (step?: number) => void;
  setSelectedFloorType: (type: FloorObjectType) => void;
  rotateFloorPlacement: (step?: number) => void;
  setSelectedMarkerType: (type: MarkerObjectType) => void;
  setSelectedPatchType: (type: TerrainPatchType) => void;
  setPatchShape: (shape: PatchShape) => void;
  setPatchEraseMode: (erase: boolean) => void;
  commitTerrainPatch: (polygon: Polygon) => void;
  eraseTerrainPatchAt: (x: number, y: number) => void;
  setRoomKey: (objectId: string, updates: RoomKeyUpdate) => void;
  deleteRoomKey: (objectId: string) => void;
  openRoomKeyPanel: (objectId: string) => void;
  closeRoomKeyPanel: () => void;
  setSelectedTerrain: (terrain: TerrainBrush) => void;
  setRepeatPlacement: (repeat: boolean) => void;
  setAnnotationKind: (kind: AnnotationKind) => void;
  setAnnotationText: (text: string) => void;
  setAnnotationFontSize: (size: number) => void;
  setAnnotationColor: (color: string) => void;
  setNextRoomNumber: (number: number) => void;
  selectObject: (id: string | null) => void;
  commitRect: (rect: Rect, mode: "add" | "subtract") => void;
  commitPolygon: (polygon: Polygon, mode: "add" | "subtract") => void;
  beginTerrainStroke: () => void;
  paintHex: (q: number, r: number, brush?: TerrainBrush) => void;
  addObject: (assetId: StampAssetId, x: number, y: number) => string;
  addAnnotation: (x: number, y: number) => string;
  beginObjectTransform: () => void;
  updateObject: (id: string, updates: Partial<MapObject>) => void;
  updateSelectedObjectDetails: (updates: Pick<Partial<MapObject>, "text" | "fontSize" | "color">) => void;
  rotateSelectedObject: (degrees: number) => void;
  resizeSelectedObject: (scale: number) => void;
  setSelectedObjectSize: (width: number, height: number) => void;
  nudgeSelectedObject: (deltaX: number, deltaY: number) => void;
  changeSelectedObjectLayer: (delta: number) => void;
  toggleSelectedObjectLock: () => void;
  duplicateSelectedObject: () => void;
  deleteSelectedObject: () => void;
  undo: () => void;
  redo: () => void;
  clearMap: () => void;
  clearCurrentMode: () => void;
  resetView: () => void;
  loadDocument: (document: MapDocument) => void;
  getDocument: () => MapDocument;
  setLanguage: (language: Language) => void;
  setCurrentMapId: (id: string | null) => void;
  /** Replaces the whole editing session with a stored map. Clears undo history. */
  openPersistedMap: (input: OpenPersistedMapInput) => void;
  applyPreferences: (preferences: PersistedPreferencesInput) => void;
  markHydrated: () => void;
}

export interface OpenPersistedMapInput {
  id: string;
  document: MapDocument;
  viewport: Viewport;
  nextRoomNumber: number;
}

export type PersistedPreferencesInput = Partial<Pick<
  MapState,
  | "language"
  | "selectedStampAsset"
  | "selectedWallType"
  | "selectedFloorType"
  | "selectedMarkerType"
  | "selectedPatchType"
  | "selectedTerrain"
  | "repeatPlacement"
  | "annotationKind"
  | "annotationText"
  | "annotationFontSize"
  | "annotationColor"
>>;

const cloneGeometry = (geometry: MultiPolygon): MultiPolygon =>
  geometry.map((polygon) =>
    polygon.map((ring) => ring.map(([x, y]) => [x, y] as [number, number])),
  );

const cloneObjects = (objects: MapObject[]): MapObject[] =>
  objects.map((object) => ({ ...object }));

const cloneHexCells = (cells: HexCell[]): HexCell[] =>
  cells.map((cell) => ({ ...cell }));

const clonePatches = (patches: TerrainPatch[]): TerrainPatch[] =>
  patches.map((patch) => ({
    ...patch,
    polygon: patch.polygon.map((ring) => ring.map(([x, y]) => [x, y] as [number, number])),
  }));

const snapshot = (
  state: Pick<MapState, "floorPolygons" | "hexCells" | "objects" | "terrainPatches" | "roomKeys">,
): MapSnapshot => ({
  geometry: cloneGeometry(state.floorPolygons),
  hexCells: cloneHexCells(state.hexCells),
  objects: cloneObjects(state.objects),
  terrainPatches: clonePatches(state.terrainPatches),
  roomKeys: cloneRoomKeys(state.roomKeys),
});

const pushSnapshot = (state: MapState): Pick<MapState, "past" | "future"> => ({
  past: [...state.past.slice(-49), snapshot(state)],
  future: [],
});


/**
 * Even-odd ray cast, honouring holes: a point inside an outer ring but also
 * inside an inner ring counts as outside, which is what "click the patch you
 * can see" means.
 */
const pointInPolygon = ([x, y]: Point, polygon: Polygon): boolean => {
  let inside = false;
  for (const ring of polygon) {
    for (let index = 0, previous = ring.length - 1; index < ring.length; previous = index, index += 1) {
      const a = ring[index];
      const b = ring[previous];
      if (!a || !b) continue;
      const intersects = (a[1] > y) !== (b[1] > y)
        && x < ((b[0] - a[0]) * (y - a[1])) / (b[1] - a[1]) + a[0];
      if (intersects) inside = !inside;
    }
  }
  return inside;
};

const objectId = () =>
  `obj_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;

export const useMapStore = create<MapState>()((set, get) => ({
  language: DEFAULT_LANGUAGE,
  currentMapId: null,
  hydrated: false,
  tool: "room",
  meta: defaultMeta,
  floorPolygons: sampleGeometry,
  hexCells: [],
  objects: [],
  terrainPatches: [],
  roomKeys: {},
  roomKeyPanelObjectId: null,
  viewport: { x: 0, y: 0, zoom: 1 },
  selectedStampAsset: "door",
  selectedWallType: DEFAULT_WALL_TYPE,
  selectedFloorType: DEFAULT_FLOOR_TYPE,
  selectedMarkerType: DEFAULT_MARKER_TYPE,
  selectedPatchType: DEFAULT_PATCH_TYPE,
  patchShape: "rect",
  patchEraseMode: false,
  floorPlacementRotation: 0,
  selectedTerrain: "plains",
  repeatPlacement: false,
  annotationKind: "text",
  annotationText: "Secret passage",
  annotationFontSize: 24,
  annotationColor: "#2d241d",
  nextRoomNumber: 1,
  selectedObjectId: null,
  past: [],
  future: [],
  terrainStrokeSnapshot: null,

  setTool: (tool) => set((state) => ({
    tool: isToolAvailableInMode(tool, state.meta.gridType)
      ? tool
      : defaultToolForMode(state.meta.gridType),
    selectedObjectId: tool === "select" ? state.selectedObjectId : null,
    terrainStrokeSnapshot: null,
  })),
  setMapMode: (mode) => set((state) => ({
    meta: {
      ...state.meta,
      gridType: mode,
      dungeonGridSizeWorld: state.meta.gridType === "square"
        ? state.meta.gridSizeWorld
        : state.meta.dungeonGridSizeWorld,
      hexGridSizeWorld: state.meta.gridType === "hex"
        ? state.meta.gridSizeWorld
        : state.meta.hexGridSizeWorld,
      gridSizeWorld: gridSizeForMode(state.meta, mode),
    },
    tool: defaultToolForMode(mode),
    selectedObjectId: null,
    terrainStrokeSnapshot: null,
  })),
  setViewport: (viewport) => set({ viewport }),
  setTitle: (title) =>
    set((state) => ({ meta: { ...state.meta, title: title.slice(0, 80) } })),
  updateMeta: (updates) => set((state) => {
    const mode = updates.gridType ?? state.meta.gridType;
    const gridUpdate = updates.gridSizeWorld === undefined
      ? {}
      : mode === "hex"
        ? { hexGridSizeWorld: updates.gridSizeWorld }
        : { dungeonGridSizeWorld: updates.gridSizeWorld };
    return {
      meta: { ...state.meta, ...updates, ...gridUpdate },
      tool: isToolAvailableInMode(state.tool, mode)
        ? state.tool
        : defaultToolForMode(mode),
      selectedObjectId: updates.gridType && updates.gridType !== state.meta.gridType
        ? null
        : state.selectedObjectId,
    };
  }),
  setPrintGridSizeMm: (size) =>
    set((state) => ({
      meta: { ...state.meta, printGridSizeMm: Math.min(50, Math.max(5, size)) },
    })),
  setSelectedStampAsset: (selectedStampAsset) => set({ selectedStampAsset }),
  setSelectedWallType: (selectedWallType) => set({ selectedWallType }),
  setSelectedFloorType: (selectedFloorType) => set({ selectedFloorType }),
  setSelectedMarkerType: (selectedMarkerType) => set({ selectedMarkerType }),
  setSelectedPatchType: (selectedPatchType) => set({ selectedPatchType, patchEraseMode: false }),
  setPatchShape: (patchShape) => set({ patchShape }),
  setPatchEraseMode: (patchEraseMode) => set({ patchEraseMode }),

  commitTerrainPatch: (polygon) => {
    const state = get();
    if (state.meta.gridType !== "square") return;
    if ((polygon[0]?.length ?? 0) < 4) return;
    const patch: TerrainPatch = {
      id: `patch_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`,
      type: state.selectedPatchType,
      polygon,
    };
    set({ terrainPatches: [...state.terrainPatches, patch], ...pushSnapshot(state) });
  },

  eraseTerrainPatchAt: (x, y) => {
    const state = get();
    // Topmost first: the last painted patch is the one the user sees.
    for (let index = state.terrainPatches.length - 1; index >= 0; index -= 1) {
      const patch = state.terrainPatches[index];
      if (patch && pointInPolygon([x, y], patch.polygon)) {
        set({
          terrainPatches: state.terrainPatches.filter((_, position) => position !== index),
          ...pushSnapshot(state),
        });
        return;
      }
    }
  },
  /**
   * Commits one edit to a room key.
   *
   * The panel calls this on blur, not per keystroke, so a paragraph of prose is
   * one undo step rather than several hundred. An update that changes nothing is
   * dropped here as well: blur fires whether or not anything was typed, and a
   * no-op commit would still burn a step of the 50-step history.
   */
  setRoomKey: (objectId, updates) => {
    const state = get();
    if (!objectId) return;
    const existing = state.roomKeys[objectId];
    const next = mergeRoomKey(existing, updates);
    if (roomKeysEqual(existing ?? {}, next)) return;
    set({
      roomKeys: { ...state.roomKeys, [objectId]: next },
      ...pushSnapshot(state),
    });
  },

  /**
   * The only thing that removes a key. Deleting the annotation orphans a key on
   * purpose (D-025) — the text outlives the number until a DM says otherwise.
   */
  deleteRoomKey: (objectId) => {
    const state = get();
    if (!(objectId in state.roomKeys)) return;
    set({
      roomKeys: Object.fromEntries(
        Object.entries(state.roomKeys).filter(([id]) => id !== objectId),
      ),
      roomKeyPanelObjectId: state.roomKeyPanelObjectId === objectId
        ? null
        : state.roomKeyPanelObjectId,
      ...pushSnapshot(state),
    });
  },

  openRoomKeyPanel: (objectId) => set({ roomKeyPanelObjectId: objectId }),
  closeRoomKeyPanel: () => set({ roomKeyPanelObjectId: null }),

  rotateFloorPlacement: (step = FLOOR_ROTATION_STEP) =>
    set((state) => ({
      floorPlacementRotation: (state.floorPlacementRotation + step + 360) % 360,
    })),
  cycleWallType: (step = 1) =>
    set((state) => ({ selectedWallType: nextWallType(state.selectedWallType, step) })),
  setSelectedTerrain: (selectedTerrain) => set({ selectedTerrain }),
  setRepeatPlacement: (repeatPlacement) => set({ repeatPlacement }),
  setAnnotationKind: (annotationKind) => set({ annotationKind }),
  setAnnotationText: (annotationText) => set({ annotationText: annotationText.slice(0, 80) }),
  setAnnotationFontSize: (annotationFontSize) => set({ annotationFontSize: Math.min(96, Math.max(10, annotationFontSize)) }),
  setAnnotationColor: (annotationColor) => set({ annotationColor }),
  setNextRoomNumber: (nextRoomNumber) => set({ nextRoomNumber: Math.max(1, Math.floor(nextRoomNumber)) }),
  selectObject: (selectedObjectId) => set({ selectedObjectId }),

  commitRect: (rect, mode) => {
    const state = get();
    if (state.meta.gridType !== "square") return;
    const next = mode === "add"
      ? addRoom(state.floorPolygons, rect)
      : subtractRoom(state.floorPolygons, rect);
    if (next === state.floorPolygons) return;
    set({ floorPolygons: next, ...pushSnapshot(state), selectedObjectId: null });
  },

  commitPolygon: (polygon, mode) => {
    const state = get();
    if (state.meta.gridType !== "square") return;
    const next = mode === "add"
      ? addPolygon(state.floorPolygons, polygon)
      : subtractPolygon(state.floorPolygons, polygon);
    if (next === state.floorPolygons) return;
    set({ floorPolygons: next, ...pushSnapshot(state), selectedObjectId: null });
  },

  beginTerrainStroke: () => {
    const state = get();
    set({ terrainStrokeSnapshot: snapshot(state) });
  },

  paintHex: (q, r, brush) => set((state) => {
    if (state.meta.gridType !== "hex") return state;
    const activeBrush = brush ?? state.selectedTerrain;
    const existingIndex = state.hexCells.findIndex((cell) => cell.q === q && cell.r === r);
    if (activeBrush === "erase") {
      return existingIndex < 0
        ? state
        : {
            hexCells: state.hexCells.filter((_, index) => index !== existingIndex),
            past: state.terrainStrokeSnapshot
              ? [...state.past.slice(-49), state.terrainStrokeSnapshot]
              : state.past,
            future: state.terrainStrokeSnapshot ? [] : state.future,
            terrainStrokeSnapshot: null,
          };
    }
    if (existingIndex >= 0 && state.hexCells[existingIndex]?.terrain === activeBrush) {
      return state;
    }
    const cell: HexCell = { q, r, terrain: activeBrush };
    const history = state.terrainStrokeSnapshot
      ? {
          past: [...state.past.slice(-49), state.terrainStrokeSnapshot],
          future: [],
          terrainStrokeSnapshot: null,
        }
      : {};
    if (existingIndex < 0) return { hexCells: [...state.hexCells, cell], ...history };
    return {
      hexCells: state.hexCells.map((existing, index) => index === existingIndex ? cell : existing),
      ...history,
    };
  }),

  addObject: (assetId, x, y) => {
    const state = get();
    const definition = getStampDefinition(assetId);
    const wall = assetId === "door" ? getWallObject(state.selectedWallType) : null;
    const floor = assetId === FLOOR_OBJECT_ASSET ? getFloorObject(state.selectedFloorType) : null;
    const marker = assetId === MARKER_OBJECT_ASSET ? getMarkerObject(state.selectedMarkerType) : null;
    const family = wall ?? floor ?? marker;
    const id = objectId();
    const width = (family?.defaultWidthCells ?? definition.widthCells) * state.meta.gridSizeWorld;
    const height = (family?.defaultHeightCells ?? definition.heightCells) * state.meta.gridSizeWorld;
    const object: MapObject = {
      id,
      assetId,
      ...(wall ? { wallType: wall.id } : {}),
      ...(floor ? { floorType: floor.id } : {}),
      ...(marker ? { markerType: marker.id } : {}),
      x: x - width / 2,
      y: y - height / 2,
      width,
      height,
      // Floor objects carry the pending placement rotation; wall objects get
      // theirs from the wall they snap to, applied by the canvas.
      rotation: floor || marker ? state.floorPlacementRotation : 0,
      zIndex: Math.max(0, ...state.objects.map((item) => item.zIndex)) + 1,
      opacity: 1,
      mode: state.meta.gridType,
    };
    set({
      objects: [...state.objects, object],
      selectedObjectId: id,
      tool: state.repeatPlacement ? state.tool : "select",
      ...pushSnapshot(state),
    });
    return id;
  },

  addAnnotation: (x, y) => {
    const state = get();
    const isNumber = state.annotationKind === "room-number";
    const text = isNumber
      ? String(state.nextRoomNumber)
      : state.annotationText.trim() || t("doc.labelFallback");
    const fontSize = state.annotationFontSize;
    const width = isNumber
      ? state.meta.gridSizeWorld * 0.72
      : Math.max(state.meta.gridSizeWorld, text.length * fontSize * 0.62 + 24);
    const height = isNumber ? width : Math.max(state.meta.gridSizeWorld * 0.62, fontSize + 18);
    const id = objectId();
    const object: MapObject = {
      id,
      assetId: isNumber ? "room_number" : "text_label",
      text,
      fontSize,
      color: state.annotationColor,
      backgroundColor: "#f8f2e4",
      x: x - width / 2,
      y: y - height / 2,
      width,
      height,
      rotation: 0,
      zIndex: Math.max(0, ...state.objects.map((item) => item.zIndex)) + 1,
      opacity: 1,
      mode: state.meta.gridType,
    };
    set({
      objects: [...state.objects, object],
      selectedObjectId: id,
      nextRoomNumber: isNumber ? state.nextRoomNumber + 1 : state.nextRoomNumber,
      tool: state.repeatPlacement ? state.tool : "select",
      ...pushSnapshot(state),
    });
    return id;
  },

  beginObjectTransform: () => {
    const state = get();
    set(pushSnapshot(state));
  },

  updateObject: (id, updates) =>
    set((state) => ({
      objects: state.objects.map((object) =>
        object.id === id ? { ...object, ...updates } : object,
      ),
    })),

  updateSelectedObjectDetails: (updates) => {
    const state = get();
    const selected = state.objects.find((object) => object.id === state.selectedObjectId);
    if (!selected || selected.locked) return;
    const nextText = updates.text === undefined ? selected.text : updates.text.slice(0, 80);
    const nextFontSize = updates.fontSize === undefined
      ? selected.fontSize
      : Math.min(96, Math.max(10, updates.fontSize));
    const isText = selected.assetId === "text_label";
    const width = isText && nextText
      ? Math.max(state.meta.gridSizeWorld, nextText.length * (nextFontSize ?? 24) * 0.62 + 24)
      : selected.width;
    const centerX = selected.x + selected.width / 2;
    set({
      objects: state.objects.map((object) => object.id === selected.id
        ? {
            ...object,
            ...updates,
            text: nextText,
            fontSize: nextFontSize,
            width,
            x: centerX - width / 2,
          }
        : object),
      ...pushSnapshot(state),
    });
  },

  rotateSelectedObject: (degrees) => {
    const state = get();
    const id = state.selectedObjectId;
    if (!id) return;
    const selected = state.objects.find((object) => object.id === id);
    if (!selected || selected.locked) return;
    set({
      objects: state.objects.map((object) => object.id === id
        ? { ...object, rotation: (object.rotation + degrees + 360) % 360 }
        : object),
      ...pushSnapshot(state),
    });
  },

  resizeSelectedObject: (scale) => {
    const state = get();
    const id = state.selectedObjectId;
    if (!id) return;
    const selected = state.objects.find((object) => object.id === id);
    if (!selected || selected.locked) return;
    set({
      objects: state.objects.map((object) => {
        if (object.id !== id) return object;
        const centerX = object.x + object.width / 2;
        const centerY = object.y + object.height / 2;
        const width = Math.max(12, object.width * scale);
        const height = Math.max(12, object.height * scale);
        return { ...object, width, height, x: centerX - width / 2, y: centerY - height / 2 };
      }),
      ...pushSnapshot(state),
    });
  },

  setSelectedObjectSize: (width, height) => {
    const state = get();
    const id = state.selectedObjectId;
    if (!id) return;
    const selected = state.objects.find((object) => object.id === id);
    if (!selected || selected.locked) return;
    const nextWidth = Math.max(8, width);
    const nextHeight = Math.max(8, height);
    const centerX = selected.x + selected.width / 2;
    const centerY = selected.y + selected.height / 2;
    set({
      objects: state.objects.map((object) => object.id === id
        ? {
            ...object,
            width: nextWidth,
            height: nextHeight,
            x: centerX - nextWidth / 2,
            y: centerY - nextHeight / 2,
          }
        : object),
      ...pushSnapshot(state),
    });
  },

  nudgeSelectedObject: (deltaX, deltaY) => {
    const state = get();
    const id = state.selectedObjectId;
    if (!id) return;
    const selected = state.objects.find((object) => object.id === id);
    if (!selected || selected.locked) return;
    set({
      objects: state.objects.map((object) => object.id === id
        ? { ...object, x: object.x + deltaX, y: object.y + deltaY }
        : object),
      ...pushSnapshot(state),
    });
  },

  changeSelectedObjectLayer: (delta) => {
    const state = get();
    const id = state.selectedObjectId;
    if (!id) return;
    const selected = state.objects.find((object) => object.id === id);
    if (!selected || selected.locked) return;
    set({
      objects: state.objects.map((object) => object.id === id
        ? { ...object, zIndex: object.zIndex + delta }
        : object),
      ...pushSnapshot(state),
    });
  },

  toggleSelectedObjectLock: () => {
    const state = get();
    const id = state.selectedObjectId;
    if (!id) return;
    set({
      objects: state.objects.map((object) => object.id === id
        ? { ...object, locked: !object.locked }
        : object),
      ...pushSnapshot(state),
    });
  },

  duplicateSelectedObject: () => {
    const state = get();
    const source = state.objects.find((object) => object.id === state.selectedObjectId);
    if (!source) return;
    const id = objectId();
    const offset = state.meta.gridSizeWorld / 2;
    set({
      objects: [...state.objects, {
        ...source,
        id,
        x: source.x + offset,
        y: source.y + offset,
        zIndex: source.zIndex + 1,
      }],
      selectedObjectId: id,
      ...pushSnapshot(state),
    });
  },

  deleteSelectedObject: () => {
    const state = get();
    if (!state.selectedObjectId) return;
    set({
      objects: state.objects.filter((object) => object.id !== state.selectedObjectId),
      selectedObjectId: null,
      ...pushSnapshot(state),
    });
  },

  undo: () => {
    const state = get();
    const previous = state.past.at(-1);
    if (!previous) return;
    set({
      floorPolygons: cloneGeometry(previous.geometry),
      hexCells: cloneHexCells(previous.hexCells),
      objects: cloneObjects(previous.objects),
      terrainPatches: clonePatches(previous.terrainPatches),
      roomKeys: cloneRoomKeys(previous.roomKeys),
      selectedObjectId: null,
      past: state.past.slice(0, -1),
      future: [snapshot(state), ...state.future].slice(0, 50),
      terrainStrokeSnapshot: null,
    });
  },

  redo: () => {
    const state = get();
    const next = state.future[0];
    if (!next) return;
    set({
      floorPolygons: cloneGeometry(next.geometry),
      hexCells: cloneHexCells(next.hexCells),
      objects: cloneObjects(next.objects),
      terrainPatches: clonePatches(next.terrainPatches),
      roomKeys: cloneRoomKeys(next.roomKeys),
      selectedObjectId: null,
      past: [...state.past.slice(-49), snapshot(state)],
      future: state.future.slice(1),
      terrainStrokeSnapshot: null,
    });
  },

  clearMap: () => {
    const state = get();
    set({
      meta: { ...state.meta, title: state.meta.gridType === "hex" ? t("doc.untitledHexcrawl") : t("doc.untitledDungeon") },
      floorPolygons: [],
      hexCells: [],
      objects: [],
      terrainPatches: [],
      // The one place keys are cleared rather than orphaned: this empties the
      // whole map, and leaving keys behind would produce a blank canvas that
      // still reports a list of orphans. Clearing a single mode does orphan
      // them, because the other canvas survives (D-025).
      roomKeys: {},
      roomKeyPanelObjectId: null,
      selectedObjectId: null,
      ...pushSnapshot(state),
      terrainStrokeSnapshot: null,
    });
  },

  clearCurrentMode: () => {
    const state = get();
    const mode = state.meta.gridType;
    const hasModeContent = mode === "hex"
      ? state.hexCells.length > 0 || state.objects.some((object) => objectBelongsToMode(object, mode))
      : state.floorPolygons.length > 0 || state.objects.some((object) => objectBelongsToMode(object, mode));
    if (!hasModeContent) return;
    set({
      floorPolygons: mode === "square" ? [] : state.floorPolygons,
      terrainPatches: mode === "square" ? [] : state.terrainPatches,
      hexCells: mode === "hex" ? [] : state.hexCells,
      objects: state.objects.filter((object) => !objectBelongsToMode(object, mode)),
      selectedObjectId: null,
      ...pushSnapshot(state),
      terrainStrokeSnapshot: null,
    });
  },

  resetView: () => set({ viewport: { x: 0, y: 0, zoom: 1 } }),

  loadDocument: (document) => {
    const state = get();
    const mode = document.meta.gridType;
    const incomingMeta = { ...defaultMeta, ...document.meta };
    const normalisedMeta: MapMeta = {
      ...incomingMeta,
      dungeonGridSizeWorld: document.meta.dungeonGridSizeWorld ?? (mode === "square" ? document.meta.gridSizeWorld : defaultMeta.gridSizeWorld),
      hexGridSizeWorld: document.meta.hexGridSizeWorld ?? (mode === "hex" ? document.meta.gridSizeWorld : defaultMeta.gridSizeWorld),
    };
    set({
      meta: { ...normalisedMeta, gridSizeWorld: gridSizeForMode(normalisedMeta, mode) },
      floorPolygons: document.geometry.floorPolygons,
      hexCells: document.geometry.hexCells ?? [],
      terrainPatches: document.geometry.terrainPatches ?? [],
      roomKeys: document.roomKeys ?? {},
      roomKeyPanelObjectId: null,
      objects: assignMissingObjectModes(document.objects, mode),
      tool: defaultToolForMode(mode),
      selectedObjectId: null,
      ...pushSnapshot(state),
      terrainStrokeSnapshot: null,
    });
  },

  getDocument: () => {
    const state = get();
    return {
      version: "1.0.0",
      meta: state.meta,
      geometry: {
        floorPolygons: state.floorPolygons,
        hexCells: state.hexCells,
        terrainPatches: state.terrainPatches,
      },
      objects: state.objects,
      roomKeys: state.roomKeys,
    };
  },

  setLanguage: (language) => set({ language }),

  setCurrentMapId: (currentMapId) => set({ currentMapId }),

  markHydrated: () => set({ hydrated: true }),

  applyPreferences: (preferences) => set((state) => ({ ...state, ...preferences })),

  openPersistedMap: ({ id, document, viewport, nextRoomNumber }) => {
    const mode = document.meta.gridType;
    const incomingMeta = { ...defaultMeta, ...document.meta };
    const normalisedMeta: MapMeta = {
      ...incomingMeta,
      dungeonGridSizeWorld: document.meta.dungeonGridSizeWorld
        ?? (mode === "square" ? document.meta.gridSizeWorld : defaultMeta.gridSizeWorld),
      hexGridSizeWorld: document.meta.hexGridSizeWorld
        ?? (mode === "hex" ? document.meta.gridSizeWorld : defaultMeta.gridSizeWorld),
    };
    set({
      currentMapId: id,
      hydrated: true,
      meta: { ...normalisedMeta, gridSizeWorld: gridSizeForMode(normalisedMeta, mode) },
      floorPolygons: document.geometry.floorPolygons,
      hexCells: document.geometry.hexCells ?? [],
      terrainPatches: document.geometry.terrainPatches ?? [],
      roomKeys: document.roomKeys ?? {},
      roomKeyPanelObjectId: null,
      objects: assignMissingObjectModes(document.objects, mode),
      viewport,
      nextRoomNumber,
      tool: defaultToolForMode(mode),
      selectedObjectId: null,
      // History belongs to an editing session, not to a saved map.
      past: [],
      future: [],
      terrainStrokeSnapshot: null,
    });
  },
}));
